/**
 * Story store - manages characters, scenes, and narrative beats.
 * State is loaded from server via fullState event. No local persistence.
 */

import { create } from "zustand";
import type {
  BeatType,
  StoryCharacterPayload,
  StoryScenePayload,
  NarrativeBeatPayload,
} from "@amzn/a4k-creator-studio-shared";

export interface StoryCharacter {
  characterId: string;
  name: string;
  visualDescription: string;
  personality?: string;
  imageId?: string;
  imageBase64?: string;
  isLoading?: boolean;
  hasError?: boolean;
  isModerated?: boolean;
}

export interface StoryScene {
  sceneId: string;
  name?: string;
  visualDescription: string;
  imageId?: string;
  imageBase64?: string;
  isLoading?: boolean;
  hasError?: boolean;
  isModerated?: boolean;
}

export interface NarrativeBeat {
  beatId: string;
  order: number;
  characterIds: string[];
  sceneId: string;
  action: string;
  visualDescription?: string;
  beatType: BeatType | string;
  imageId?: string;
  imageBase64?: string;
  imageLoading?: boolean;
  isModerated?: boolean;
}

interface StoryState {
  storyId: string | null;
  title: string | null;
  characters: StoryCharacter[];
  scenes: StoryScene[];
  beats: NarrativeBeat[];
  isLoaded: boolean;
  focusedCharacterId: string | null;
  focusedSceneId: string | null;
  /** Timestamp of last manual (click) focus, used to prevent voice from overriding */
  manualFocusUntil: number;
  /** Until this time, voice/image events can't set focus — keeps the grid shown
   *  while a loaded project's replayed image events arrive. */
  suppressFocusUntil: number;

  /** Replace all state from server fullState event */
  replaceAll: (data: {
    storyId: string | null;
    title: string | null;
    characters: StoryCharacter[];
    scenes: StoryScene[];
    beats: NarrativeBeat[];
  }) => void;
  setStory: (storyId: string, title?: string) => void;
  setCharacters: (characters: StoryCharacter[]) => void;
  setScenes: (scenes: StoryScene[]) => void;
  setBeats: (beats: NarrativeBeat[]) => void;
  addCharacter: (char: StoryCharacter) => void;
  updateCharacter: (characterId: string, updates: Partial<StoryCharacter>) => void;
  removeCharacter: (characterId: string) => void;
  addScene: (scene: StoryScene) => void;
  updateScene: (sceneId: string, updates: Partial<StoryScene>) => void;
  removeScene: (sceneId: string) => void;
  addBeat: (beat: NarrativeBeat) => void;
  updateBeat: (beatId: string, updates: Partial<NarrativeBeat>) => void;
  removeBeat: (beatId: string) => void;
  setFocusedCharacter: (characterId: string | null) => void;
  setFocusedScene: (sceneId: string | null) => void;
  /** Set focus from a manual click — takes priority over voice for 5s */
  setManualFocus: (entityType: "character" | "scene", entityId: string) => void;
  /** Set focus from voice/AI — only applies if no recent manual focus */
  setVoiceFocus: (characterId: string) => void;
  /** Clear all focus */
  clearFocus: () => void;
  restoreStory: (story: {
    storyId?: string;
    title?: string;
    characters: StoryCharacter[];
    scenes: StoryScene[];
    beats: NarrativeBeat[];
  }) => void;
  clearAll: () => void;
  /** Like clearAll but keeps storyId — empties the project without deleting it. */
  clearContent: () => void;
}

function normalizeCharacter(c: StoryCharacterPayload & { id?: string }): StoryCharacter {
  return {
    characterId: c.characterId || c.id || "",
    name: c.name,
    visualDescription: c.visualDescription,
    personality: c.personality,
    imageId: c.imageId,
    imageBase64: c.imageBase64,
  };
}

function normalizeScene(s: StoryScenePayload & { id?: string }): StoryScene {
  return {
    sceneId: s.sceneId || s.id || "",
    name: s.name,
    visualDescription: s.visualDescription,
    imageId: s.imageId,
    imageBase64: s.imageBase64,
  };
}

function normalizeBeat(b: NarrativeBeatPayload & { id?: string }): NarrativeBeat {
  return {
    beatId: b.beatId || b.id || "",
    order: b.order,
    characterIds: b.characterIds,
    sceneId: b.sceneId,
    action: b.action,
    visualDescription: b.visualDescription,
    beatType: b.beatType,
    imageId: b.imageId,
    imageBase64: b.imageBase64,
  };
}

export const useStoryStore = create<StoryState>((set, get) => ({
  storyId: null,
  title: null,
  characters: [],
  scenes: [],
  beats: [],
  isLoaded: false,
  focusedCharacterId: null,
  focusedSceneId: null,
  manualFocusUntil: 0,
  suppressFocusUntil: 0,

  replaceAll: (data) => {
    const characters = (data.characters || []).map(normalizeCharacter);
    const scenes = (data.scenes || []).map(normalizeScene);
    const beats = (data.beats || []).map(normalizeBeat).sort((a, b) => a.order - b.order);
    set({
      storyId: data.storyId,
      title: data.title,
      characters,
      scenes,
      beats,
      isLoaded: true,
      focusedCharacterId: null,
      focusedSceneId: null,
      // Show the grid on load: ignore voice/image focus events that the server
      // replays right after a project loads.
      suppressFocusUntil: Date.now() + 3000,
    });
  },

  setStory: (storyId, title) => {
    set({ storyId, title });
  },

  setCharacters: (characters) => {
    set({ characters: characters.map(normalizeCharacter) });
  },

  setScenes: (scenes) => {
    set({ scenes: scenes.map(normalizeScene) });
  },

  setBeats: (beats) => {
    set({ beats: beats.map(normalizeBeat).sort((a, b) => a.order - b.order) });
  },

  addCharacter: (char) => {
    set((s) => ({
      characters: s.characters.some((c) => c.characterId === char.characterId)
        ? s.characters.map((c) => (c.characterId === char.characterId ? { ...c, ...char } : c))
        : [...s.characters, char],
    }));
  },

  updateCharacter: (characterId, updates) => {
    set((s) => ({
      characters: s.characters.map((c) => (c.characterId === characterId ? { ...c, ...updates } : c)),
    }));
  },

  removeCharacter: (characterId) => {
    set((s) => ({ characters: s.characters.filter((c) => c.characterId !== characterId) }));
  },

  addScene: (scene) => {
    set((s) => ({
      scenes: s.scenes.some((sc) => sc.sceneId === scene.sceneId)
        ? s.scenes.map((sc) => (sc.sceneId === scene.sceneId ? { ...sc, ...scene } : sc))
        : [...s.scenes, scene],
    }));
  },

  updateScene: (sceneId, updates) => {
    set((s) => ({
      scenes: s.scenes.map((sc) => (sc.sceneId === sceneId ? { ...sc, ...updates } : sc)),
    }));
  },

  removeScene: (sceneId) => {
    set((s) => ({ scenes: s.scenes.filter((sc) => sc.sceneId !== sceneId) }));
  },

  addBeat: (beat) => {
    set((s) => ({
      beats: (s.beats.some((b) => b.beatId === beat.beatId)
        ? s.beats.map((b) => (b.beatId === beat.beatId ? { ...b, ...beat } : b))
        : [...s.beats, beat]
      ).sort((a, b) => a.order - b.order),
    }));
  },

  updateBeat: (beatId, updates) => {
    set((s) => ({
      beats: s.beats.map((b) => (b.beatId === beatId ? { ...b, ...updates } : b)).sort((a, b) => a.order - b.order),
    }));
  },

  removeBeat: (beatId) => {
    set((s) => ({ beats: s.beats.filter((b) => b.beatId !== beatId) }));
  },

  setFocusedCharacter: (characterId) => set({ focusedCharacterId: characterId }),

  setFocusedScene: (sceneId) => set({ focusedSceneId: sceneId }),

  setManualFocus: (entityType, entityId) => {
    // Toggle: tapping the already-focused card clears focus (back to the grid),
    // matching the prototype's select behavior.
    const state = get();
    const current = entityType === "character" ? state.focusedCharacterId : state.focusedSceneId;
    const next = current === entityId ? null : entityId;
    set(
      entityType === "character"
        ? { focusedCharacterId: next, manualFocusUntil: Date.now() + 5000 }
        : { focusedSceneId: next, manualFocusUntil: Date.now() + 5000 },
    );
  },

  setVoiceFocus: (characterId) => {
    const now = Date.now();
    if (now < get().manualFocusUntil || now < get().suppressFocusUntil) return;
    set({ focusedCharacterId: characterId });
  },

  clearFocus: () => set({ focusedCharacterId: null, focusedSceneId: null }),

  restoreStory: (story) => {
    const storyId = story.storyId || null;
    const title = story.title || null;
    const characters = (story.characters || []).map(normalizeCharacter);
    const scenes = (story.scenes || []).map(normalizeScene);
    const beats = (story.beats || []).map(normalizeBeat).sort((a, b) => a.order - b.order);
    set({
      storyId,
      title,
      characters,
      scenes,
      beats,
      focusedCharacterId: null,
      focusedSceneId: null,
      isLoaded: true,
      // Same as replaceAll: ignore the focus events the server replays right
      // after a saved project is restored, so the grid stays shown.
      suppressFocusUntil: Date.now() + 3000,
    });
  },

  clearAll: () => {
    set({
      storyId: null,
      title: null,
      characters: [],
      scenes: [],
      beats: [],
      focusedCharacterId: null,
      focusedSceneId: null,
    });
  },

  clearContent: () => {
    set({
      title: null,
      characters: [],
      scenes: [],
      beats: [],
      focusedCharacterId: null,
      focusedSceneId: null,
    });
  },
}));
